import {
    AuthContext,
    DataTable,
    DataTableCell,
    DataTablePresenter,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    ListProviderReceiveFaxesRequest,
    ProviderFaxSortType,
    ProviderReceiveFax,
} from '@enter/core';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import { ReceiveFaxDetail } from 'pages/main/claims/components/claim-timeline-table/receive-fax-detail';
import React, { useContext } from 'react';
import { Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, IObservedRootStore, parseSortBy } from 'stores';
import * as S from './inbox.styles';

interface IFaxProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
}

interface IFaxState {
    loading: boolean;
    options: ITableFetchOptions | undefined;
}

const columns: ITableColumn<ProviderReceiveFax>[] = [
    {
        id: 'External ID',
        Header: 'ExternalId',
        sortType: ProviderFaxSortType.EXTERNALID,
        defaultSort: { id: 'External ID', desc: true },
        accessor: 'externalId',
        Cell: DataTableCell('String'),
        show: false,
    },
    {
        id: 'FromE164',
        Header: 'Sender phone',
        accessor: 'fromE164',
        Cell: DataTableCell('String'),
        disableSortBy: true,
    },
    {
        id: 'ToE164',
        Header: 'Receiver phone',
        accessor: 'toE164',
        Cell: DataTableCell('String'),
        disableSortBy: true,
    },
    {
        id: 'Pages',
        Header: '# of Pages',
        accessor: 'pages',
        Cell: DataTableCell('String'),
        disableSortBy: true,
    },
    {
        id: 'Received',
        Header: 'Received date',
        accessor: 'received',
        Cell: DataTableCell('DateTime'),
        disableSortBy: true,
    },
    {
        id: 'CreatedDate',
        sortType: ProviderFaxSortType.CREATED,
        Header: 'Created Date',
        accessor: 'created',
        Cell: DataTableCell('DateTime'),
        show: false,
    },
];
const filterSchemas: IFilterSchema<keyof ListProviderReceiveFaxesRequest>[] = [
    {
        label: 'Claim ID',
        filterId: 'filterClaimId',
        columnId: 'claimId',
        filterType: 'String',
    },
    {
        label: 'Document Id',
        filterId: 'filterDocumentId',
        columnId: 'documentId',
        filterType: 'String',
    },
    {
        label: 'Pages',
        filterId: 'filterPages',
        columnId: 'pages',
        filterType: 'Number',
    },
    {
        label: 'Created Date',
        filterId: 'filterCreated',
        columnId: 'created',
        filterType: 'Date',
    },
];

class FaxPage extends React.Component<IFaxProps, IFaxState> {
    private presenter: DataTablePresenter<ProviderReceiveFax>;

    constructor(props: IFaxProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
        };
        this.presenter = new DataTablePresenter<ProviderReceiveFax>({
            name: 'fax-receive',
            data: [],
            columns,
            filterSchemas,
        });
    }

    public componentDidMount(): void {
        document.title = 'Provider Dashboard - Received Faxes';
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('fax-receive');
    }

    public render(): React.ReactElement {
        return (
            <DataTable
                presenter={this.presenter}
                isLoading={this.state.loading}
                onFetch={this.onFetch}
                onRowClick={this.onRowClick}
                renderRowSubComponent={this.renderRowSubComponent}
            />
        );
    }

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        if (!_.isEqual(this.state.options, fetchOptions)) {
            try {
                this.setState({
                    options: fetchOptions,
                    loading: true,
                });
                this.authContext.cancelGroup('fax-receive');
                this.authContext.setGroup('fax-receive');

                const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
                const sortAndOrder = parseSortBy(sortBy, columns);
                const faxListing = await this.props.rootStore.claimStore.loadReceiveFaxListing({
                    page: pageIndex,
                    pageSize,
                    ...sortAndOrder,
                    filters: validFilters,
                });
                this.presenter.onDataUpdate(faxListing.items || []);
                this.presenter.onDataTotalChange(faxListing.total || 0);
                this.setState({
                    loading: false,
                });
            } catch (err) {
                this.dialogContext.showError(err);
            }
        }
    };

    private onRowClick = async (row: Row<ProviderReceiveFax>): Promise<void> => {
        // retrieve mail cart if necessary
        row.toggleRowExpanded();
    };

    private renderRowSubComponent = (row: Row<ProviderReceiveFax>): React.ReactElement => {
        return (
            <S.Expand>
                <ReceiveFaxDetail receiveFax={row.original} />
            </S.Expand>
        );
    };
}

export const InboxFax: React.FC<IObservedRootStore> = inject('rootStore')(
    observer(
        (props: IObservedRootStore): React.ReactElement => {
            const { rootStore } = props;
            if (!rootStore) {
                return <></>;
            }
            const authContext = useContext(AuthContext);
            const dialogContext = useContext(DialogContext);
            return <FaxPage authContext={authContext} dialogContext={dialogContext} rootStore={rootStore} />;
        },
    ),
);
